import { CompozzError, isCompozzError } from "@dto/CompozzError";
import Field, { AggregationType, FieldType, FieldTypeChanges, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_RELATION_KEY } from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { CONTAINS, Direction, Edge, EQUAL, FieldKey, HAS, INBOUND, INF, INF_EQUAL, IS_EMPTY, IS_NOT_EMPTY, NOT_CONTAINS, NOT_EQUAL, NOT_HAS, Operator, OUTBOUND, Path, SUP, SUP_EQUAL } from "@dto/records";
import { ObjectRelation } from "@dto/relation";
import { GetRoleRequest, normalizeRole, RightType, Role } from "@dto/Users";
import { TFunction } from "i18next";
import { BASE_URL, DIRECTION_SEPARATOR, FIELD_SEPARATOR, PATH_SEPARATOR, SOURCE_SEPARATOR } from "./constants";
import { toastError } from "./toasts";
import { idToKey } from "./types";
import { get, post, put } from "./utils";
import { Dashboard } from "@dto/Dashboard";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";
import { View } from "@dto/views";
import { UnresolvedReference, Workspace, WORKSPACE_IMPORT_UNRESOLVED_REFERENCES, WorkspaceFileForImport, WorkspacesForImport, WorkspacesOrder } from "@dto/workspaces";

// Key returns a unique key for the path
// It is used to identify a path in a map
export function pathKey(path: Path) {
    let key = "";
    for (const edge of path.edges) {
        key += edge.key + DIRECTION_SEPARATOR + edge.direction + PATH_SEPARATOR
    }
    key += path.object + FIELD_SEPARATOR + path.field
    if (path.srcField && path.srcObject) {
        key = path.srcObject + FIELD_SEPARATOR + path.srcField + SOURCE_SEPARATOR + key
    } else {
        key = SOURCE_SEPARATOR + key
    }
    return key
}

export function getFieldKey(pathKey: string): FieldKey | undefined {
    const sourceParts = pathKey.split(SOURCE_SEPARATOR);
    if (sourceParts.length > 1) {
        pathKey = sourceParts[1];
    }
    const parts = pathKey.split(PATH_SEPARATOR);
    const objAndField = parts[parts.length - 1];
    const objAndFieldParts = objAndField.split(FIELD_SEPARATOR);
    if (objAndFieldParts.length < 2) {
        return undefined;
    }
    return {
        objectKey: objAndFieldParts[0],
        fieldKey: objAndFieldParts[1]
    }
}

export function getFieldLeafKey(fieldKey: string, fieldsByKeys: Map<string, Field> | null): string | undefined {

    const field = fieldsByKeys?.get(fieldKey);
    if (!field) {
        return undefined;
    }
    if (field.type !== FieldType.Link) {
        return fieldKey;
    }
    return getFieldLeafKey(field.typeItems?.get(TYPE_ITEMS_FIELD_KEY), fieldsByKeys);
}

export function getSrcFieldKey(pathKey: string): FieldKey | undefined {
    const sourceParts = pathKey.split(SOURCE_SEPARATOR);
    if (sourceParts.length !== 2) {
        return undefined;
    }
    let rootPathKey = sourceParts[0];
    if (rootPathKey === "") {
        rootPathKey = sourceParts[1];
    }
    const parts = rootPathKey.split(PATH_SEPARATOR);
    const objAndField = parts[parts.length - 1];
    const objAndFieldParts = objAndField.split(FIELD_SEPARATOR);
    if (objAndFieldParts.length < 2) {
        return undefined;
    }
    return {
        objectKey: objAndFieldParts[0],
        fieldKey: objAndFieldParts[1]
    }
}

export function getPathKeys(objectInfos: ObjectInfos | null, fields: Field[] | null, relationByKeys: Map<string, ObjectRelation> | null, fieldByKeys: Map<string, Field> | null, fieldKeyValueIfInvalid: boolean): (string | null)[] {
    if (!objectInfos || !fields) {
        return [];
    }
    const pathKeys: (string | null)[] = [];
    if (objectInfos?.object?._key) {
        fields?.forEach((field) => {
            const path = fieldToPath(objectInfos?.object?._key, relationByKeys, fieldByKeys, field);
            if (path) {
                pathKeys.push(pathKey(path));
            } else {
                pathKeys.push(fieldKeyValueIfInvalid ? field._key : null)
            }
        });
    }
    return pathKeys;
}

export function getFieldByPathKeys(objectInfos: ObjectInfos | null, fields: Field[] | null, relationByKeys: Map<string, ObjectRelation> | null, fieldByKeys: Map<string, Field> | null) {
    if (!objectInfos || !fields) {
        return new Map<string, Field>();
    }
    const result = new Map<string, Field>();
    if (objectInfos?.object?._key) {
        fields?.forEach((field) => {
            const path = fieldToPath(objectInfos?.object?._key, relationByKeys, fieldByKeys, field);
            if (path) {
                result.set(pathKey(path), field);
            }
        });
    }
    return result;
}

export function getIndexByPathKeys(objectInfos: ObjectInfos | null, fields: Field[] | null, relationByKeys: Map<string, ObjectRelation> | null, fieldByKeys: Map<string, Field> | null) {
    if (!objectInfos || !fields) {
        return new Map<string, number>();
    }
    const result = new Map<string, number>();
    if (objectInfos?.object?._key) {
        fields?.forEach((field, index) => {
            const path = fieldToPath(objectInfos?.object?._key, relationByKeys, fieldByKeys, field);
            if (path) {
                result.set(pathKey(path), index);
            }
        });
    }
    return result;
}

export function getFieldAndIndexByPathKeys(objectInfos: ObjectInfos | null, fields: Field[] | null, relationByKeys: Map<string, ObjectRelation> | null, fieldByKeys: Map<string, Field> | null): Map<string, { field: Field, index: number }> {
    if (!objectInfos || !fields) {
        return new Map<string, { field: Field, index: number }>();
    }
    const result = new Map<string, { field: Field, index: number }>();
    if (objectInfos?.object?._key) {
        fields?.forEach((field, index) => {
            const path = fieldToPath(objectInfos?.object?._key, relationByKeys, fieldByKeys, field);
            if (path) {
                result.set(pathKey(path), { field: field, index: index });
            }
        });
    }
    return result;
}

export function isFieldValid(field: Field, objectKey: string, relationByKeys: Map<string, ObjectRelation> | null, fieldByKeys: Map<string, Field> | null): boolean {
    if (field.type !== FieldType.Link) {
        return true;
    }
    if (!field.typeItems) {
        return false;
    }
    const relationKey = field.typeItems.get(TYPE_ITEMS_RELATION_KEY);
    if (!relationKey) {
        return false;
    }
    const relation = relationByKeys?.get(relationKey);
    if (!relation) {
        return false;
    }
    const fieldKey = field.typeItems.get(TYPE_ITEMS_FIELD_KEY);
    if (!fieldKey) {
        return false;
    }
    const destField = fieldByKeys?.get(fieldKey);
    if (!destField) {
        return false;
    }
    let targetObject = relation._to.split("/")[1];

    if (targetObject === objectKey) {
        targetObject = relation._from.split("/")[1];
    }
    const destFieldPath = fieldToPath(targetObject, relationByKeys, fieldByKeys, destField);
    if (!destFieldPath) {
        return false;
    }
    return true;
}

export function fieldToPath(objectKey: string, relationByKeys: Map<string, ObjectRelation> | null, fieldByKeys: Map<string, Field> | null, field: Field): Path | null {
    if (!isFieldValid(field, objectKey, relationByKeys, fieldByKeys)) {
        return null;
    }
    if (field.type !== FieldType.Link) {
        return {
            edges: [],
            object: objectKey,
            field: field._key,
            srcField: null,
            srcObject: null,
            aggregation: field.aggregation || AggregationType.NO_AGGR
        }
    } else {
        const typeItems = field.typeItems as Map<string, any>;
        if (typeItems) {
            const relationKey = typeItems.get(TYPE_ITEMS_RELATION_KEY);
            const fieldKey = typeItems.get(TYPE_ITEMS_FIELD_KEY);
            if (relationKey && fieldKey) {
                const relation = relationByKeys?.get(relationKey);
                if (relation) {
                    let direction: Direction = OUTBOUND;
                    let targetObject = relation._to.split("/")[1];
                    if (targetObject === objectKey) {
                        direction = INBOUND;
                        targetObject = relation._from.split("/")[1];
                    }
                    let result: Path = {
                        edges: [{ key: relationKey, direction: direction }],
                        object: targetObject,
                        field: fieldKey,
                        srcField: field._key,
                        srcObject: objectKey,
                        aggregation: field.aggregation || AggregationType.NO_AGGR
                    }
                    const destField = fieldByKeys?.get(fieldKey);
                    if (destField) {
                        if (destField.type === FieldType.Link) {
                            const destFieldPath = fieldToPath(targetObject, relationByKeys, fieldByKeys, destField);
                            if (destFieldPath) {
                                return {
                                    edges: [...result.edges, ...destFieldPath.edges],
                                    object: destFieldPath.object,
                                    field: destFieldPath.field,
                                    srcField: result.srcField,
                                    srcObject: result.srcObject,
                                    // Informational only: the server rebuilds the path from
                                    // its key and resolves the column aggregation from the
                                    // view map, then from the ROOT link field (srcField).
                                    aggregation: destField.aggregation || AggregationType.NO_AGGR
                                }
                            }
                        } else {
                            return result;
                        }
                    }
                    return null;
                }
            }
        }
        return null;
    }
}

export function getValidOperator(type: FieldType | undefined, typeItems: Map<string, any> | undefined): Operator[] {
    if (!type) {
        return [];
    }

    const fieldByKeys = useModelStore.getState().fieldByKeys;
    if (type === FieldType.Link) {
        const fieldKey = typeItems?.get(TYPE_ITEMS_FIELD_KEY);
        const destField = fieldByKeys?.get(fieldKey);
        if (destField?.type !== FieldType.Text) {
            return getValidOperator(destField?.type, destField?.typeItems);
        }
    }

    switch (type) {
        case FieldType.Text:
            return [CONTAINS, NOT_CONTAINS, EQUAL, NOT_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.TextArea:
            return [CONTAINS, NOT_CONTAINS, EQUAL, NOT_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.Url:
            return [CONTAINS, NOT_CONTAINS, EQUAL, NOT_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.Number:
            return [EQUAL, NOT_EQUAL, INF, SUP, INF_EQUAL, SUP_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.Decimal:
            return [EQUAL, NOT_EQUAL, INF, SUP, INF_EQUAL, SUP_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.Devise:
            return [EQUAL, NOT_EQUAL, INF, SUP, INF_EQUAL, SUP_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.Percent:
            return [EQUAL, NOT_EQUAL, INF, SUP, INF_EQUAL, SUP_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.Date:
            return [EQUAL, NOT_EQUAL, INF, SUP, INF_EQUAL, SUP_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.Boolean:
            return [EQUAL];
        case FieldType.Link:
            return [EQUAL, NOT_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.Choice:
            return [EQUAL, NOT_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.MultiChoice:
            return [HAS, NOT_HAS, IS_EMPTY, IS_NOT_EMPTY];
        case FieldType.User:
            return [EQUAL, NOT_EQUAL, IS_EMPTY, IS_NOT_EMPTY];
    }
    return [];
}

export function isSortable(type: FieldType): boolean {
    switch (type) {
        case FieldType.Text:
            return true;
        case FieldType.TextArea:
            return true;
        case FieldType.Url:
            return true;
        case FieldType.Number:
            return true;
        case FieldType.Decimal:
            return true;
        case FieldType.Devise:
            return true;
        case FieldType.Percent:
            return true;
        case FieldType.Date:
            return true;
        case FieldType.Boolean:
            return true;
        case FieldType.Link:
            return true;
        case FieldType.Choice:
            return true;
        case FieldType.MultiChoice:
            return true;
        case FieldType.User:
            return true;
        case FieldType.Period:
            return true;
        default:
            return false;
    }
}


export function getOperatorLabel(operator: Operator, t: TFunction): string {

    switch (operator) {
        case EQUAL:
            return "=";
        case NOT_EQUAL:
            return "≠";
        case INF:
            return "<";
        case SUP:
            return ">";
        case INF_EQUAL:
            return "≤";
        case SUP_EQUAL:
            return "≥";
        case CONTAINS:
            return t("filter.operator.contains");
        case NOT_CONTAINS:
            return t("filter.operator.not_contains");
        case HAS:
            return t("filter.operator.has");
        case NOT_HAS:
            return t("filter.operator.not_has");
        case IS_EMPTY:
            return t("filter.operator.is_empty");
        case IS_NOT_EMPTY:
            return t("filter.operator.is_not_empty");
    }
    return "";
}

export function getRelationDirection(relation: ObjectRelation | undefined, srcObjectKey: string | undefined): Direction {
    let direction: Direction = OUTBOUND;
    if (relation && srcObjectKey) {
        const to = idToKey(relation._to);
        if (to === srcObjectKey) {
            direction = INBOUND;
        }
    }
    return direction;
}

export function hasOnlyOneValue(relation: ObjectRelation | undefined, srcObjectKey: string | undefined): boolean {
    let direction: Direction = getRelationDirection(relation, srcObjectKey);

    var onlyOneValue = false;
    if (relation && ((direction === OUTBOUND && relation.destCard === 1) || (direction === INBOUND && relation.srcCard === 1))) {
        onlyOneValue = true;
    }
    return onlyOneValue;
}

export function fetchRoles(workspaceKey: string | undefined, rightType: RightType | null, recordKey: string | null, setRoles: (roles: Role[]) => void) {
    if (!workspaceKey) {
        return setRoles([]);
    }
    const getRoleRequest: GetRoleRequest = {
        rightType: rightType,
        recordKey: recordKey
    }

    post<Role[]>(`${BASE_URL}/model/workspaces/${workspaceKey}/roles/search`, getRoleRequest).then(response => {
        if (response) {
            if (isCompozzError(response)) {
                toastError(response.message);
            } else {
                setRoles((response as Role[]).map(normalizeRole));
            }
        }
    });
}

type CheckFormulaResponse = {
    valid: boolean;
    errorI18n: string;
    errorMessage: string;
    errorParams: any[];
}


export function checkFormula(workspaceKey: string | undefined, objectKey: string | undefined, fieldKey: string | undefined, formula: string | undefined, onValid: () => void, onInvalid: (errorI18n: string, errorMessage: string, errorParams: any[]) => void) {
    if (!workspaceKey || !objectKey) {
        return false;
    }
    return post<CheckFormulaResponse>(`${BASE_URL}/model/workspaces/${workspaceKey}/objects/${objectKey}/fields/checkformula`, { formula: formula, fieldKey: fieldKey || "" }).then(response => {
        if (response) {
            if (isCompozzError(response)) {
                const cError = response as CompozzError;
                onInvalid(cError.i18n, cError.message, cError.params);
            } else {
                const checkFormulaResponse = response as CheckFormulaResponse;
                if (checkFormulaResponse.valid) {
                    onValid();
                } else {
                    onInvalid(checkFormulaResponse.errorI18n, checkFormulaResponse.errorMessage, checkFormulaResponse.errorParams);
                }
            }
        }
    });
}

export function pathKeyToPath(pathKey: string | null | undefined, relationByKeys: Map<string, ObjectRelation> | null): Path | null {

    if (!pathKey) {
        return null;
    }

    const sourceParts = pathKey.split(SOURCE_SEPARATOR);
    let srcObjectKey = null;
    let srcFieldKey = null;
    if (sourceParts.length > 1) {
        let srcKeys = sourceParts[0];
        const srcKeysParts = srcKeys.split(FIELD_SEPARATOR);
        srcObjectKey = srcKeysParts[0];
        srcFieldKey = srcKeysParts[1];
        pathKey = sourceParts[1];
    }
    const pathParts = pathKey.split(FIELD_SEPARATOR);
    const pathRoot = pathParts[0];
    const pathRelations = pathRoot.split(PATH_SEPARATOR);
    const pathFieldKey = pathParts[1];

    let result: Path | null = null;
    if (pathRelations.length === 0) {
        return null;
    }
    if (pathRelations.length === 1) {
        const objectKey = pathRelations[0];
        result = {
            edges: [],
            object: objectKey,
            field: pathFieldKey,
            srcField: null,
            srcObject: null,
            aggregation: AggregationType.NO_AGGR
        }
    } else {
        const edges: Edge[] = [];
        let firstRelSrcObjectKey = null;
        for (let i = 0; i < pathRelations.length - 1; i++) {
            const relationKey = pathRelations[i].split(DIRECTION_SEPARATOR)[0];
            const direction = pathRelations[i].split(DIRECTION_SEPARATOR)[1] as Direction;
            const relation = relationByKeys?.get(relationKey);
            if (relation) {
                edges.push({
                    key: relation._key,
                    direction: direction
                });
                if (i === 0) {
                    if (direction === OUTBOUND) {
                        firstRelSrcObjectKey = idToKey(relation._from);
                    } else {
                        firstRelSrcObjectKey = idToKey(relation._to);
                    }
                }
            }
        }

        if (srcObjectKey !== firstRelSrcObjectKey) {
            srcObjectKey = firstRelSrcObjectKey;
        }

        const destObjectKey = pathRelations[pathRelations.length - 1];
        result = {
            edges: edges,
            object: destObjectKey,
            field: pathFieldKey,
            srcField: srcFieldKey,
            srcObject: srcObjectKey,
            aggregation: AggregationType.NO_AGGR
        }
    }
    return result;
}

export function getDashboards(workspaceKey: string | undefined, setDashboards: (dashboards: Dashboard[]) => void, t: TFunction) {
    if (!workspaceKey) {
        return setDashboards([]);
    }
    get<Dashboard[]>(`${BASE_URL}/model/workspaces/${workspaceKey}/dashboards`).then(response => {
        if (response) {
            if (isCompozzError(response)) {
                const cError = response as CompozzError;
                if (cError.i18n) {
                    toastError(translate(t, cError.i18n, cError.params));
                } else {
                    toastError(cError.message);
                }
            } else {
                setDashboards(response as Dashboard[]);
            }
        }
    });
}


// Fetches the type changes currently possible for a field (any type on an empty column, safe transitions otherwise)
export function fetchFieldTypeChanges(workspaceKey: string | undefined, objectKey: string | undefined, fieldKey: string, setTypeChanges: (typeChanges: FieldTypeChanges) => void) {
    if (!workspaceKey || !objectKey) {
        return;
    }
    get<FieldTypeChanges>(`${BASE_URL}/model/workspaces/${workspaceKey}/objects/${objectKey}/fields/${fieldKey}/typechanges`).then(response => {
        if (response) {
            if (isCompozzError(response)) {
                const cError = response as CompozzError;
                toastError(cError.message);
            } else {
                setTypeChanges(response as FieldTypeChanges);
            }
        }
    });
}

// Imports one or several workspaces from their Compozz files, as one operation: workspaces which depend
// on each other cannot be imported one after the other. It is all or nothing.
// The import is refused, before anything is created, when the files reference elements of shared
// workspaces which are neither in the tenant nor among the files: onUnresolvedReferences receives their list.
// Every other error goes to onError (null when the server gave no answer).
export function importWorkspaces(files: WorkspaceFileForImport[], onSuccess: (workspaces: Workspace[]) => void, onUnresolvedReferences: (references: UnresolvedReference[]) => void, onError: (cError: CompozzError | null) => void, onDone: () => void) {
    const workspacesForImport: WorkspacesForImport = {
        files: files
    }
    post<Workspace[]>(`${BASE_URL}/model/workspaces/import/batch`, workspacesForImport).then(response => {
        if (!response) {
            onError(null);
        } else if (isCompozzError(response)) {
            const cError = response as CompozzError;
            if (cError.i18n === WORKSPACE_IMPORT_UNRESOLVED_REFERENCES && Array.isArray(cError.details)) {
                onUnresolvedReferences(cError.details as UnresolvedReference[]);
            } else {
                onError(cError);
            }
        } else {
            onSuccess(response as Workspace[]);
        }
    }).catch((error) => {
        console.error(error);
        onError(null);
    }).finally(() => {
        onDone();
    });
}

// Displays the workspaces of the tenant in the order of the given keys. The order is shared by every user
// of the tenant: the server only accepts it from an owner of the tenant.
// onSuccess receives all the workspaces of the tenant in their new order. The error is reported to the
// user, then onFailure lets the caller restore the order it displayed before.
export function reorderWorkspaces(workspaceKeys: string[], onSuccess: (workspaces: Workspace[]) => void, onFailure: () => void, t: TFunction) {
    const workspacesOrder: WorkspacesOrder = {
        keys: workspaceKeys
    }
    put<Workspace[]>(`${BASE_URL}/model/workspaces/order`, workspacesOrder).then(response => {
        if (response && !isCompozzError(response)) {
            onSuccess(response as Workspace[]);
            return;
        }
        if (response) {
            const cError = response as CompozzError;
            if (cError.i18n) {
                toastError(translate(t, cError.i18n, cError.params));
            } else {
                toastError(cError.message);
            }
        }
        onFailure();
    });
}

// Orders the fields of an object as the given keys (the fields which are not
// listed go after them). The server writes the order of all the fields at once.
// onSuccess receives all the fields of the object in their new order. The error
// is reported to the user, then onFailure lets the caller keep its draft.
export function reorderFields(workspaceKey: string, objectKey: string, fieldKeys: string[], onSuccess: (fields: Field[]) => void, onFailure: () => void, t: TFunction) {
    put<Field[]>(`${BASE_URL}/model/workspaces/${workspaceKey}/objects/${objectKey}/fields/order`, { keys: fieldKeys }).then(response => {
        if (response && !isCompozzError(response)) {
            onSuccess(response as Field[]);
            return;
        }
        if (response) {
            const cError = response as CompozzError;
            if (cError.i18n) {
                toastError(translate(t, cError.i18n, cError.params));
            } else {
                toastError(cError.message);
            }
        }
        onFailure();
    });
}

export function fetchViewInfos(workspaceKey: string, objectKey: string, viewKey: string, setViewInfos: (viewInfos: View) => void) {
    get<View>(`${BASE_URL}/model/workspaces/${workspaceKey}/objects/${objectKey}/views/${viewKey}`).then(response => {
        if (response) {
            if (isCompozzError(response)) {
                const cError = response as CompozzError;
                toastError(cError.message);
            } else {
                setViewInfos(response as View);
            }
        }
    });
}


export function getHiddenPathKeys(view: View | null, allPathKeys: string[]): string[] {

    if (!view) {
        return []
    }

    if (view.shownPaths) {
        return allPathKeys.filter(pathKey => !view.shownPaths!.includes(pathKey) && allPathKeys.includes(pathKey))
    }

    if (view.hiddenPaths) {
        return view.hiddenPaths!.filter(pathKey => allPathKeys.includes(pathKey))
    }

    return []
}

export function getShownPathKeys(view: View | null, allPathKeys: string[]): string[] {

    if (!view) {
        return allPathKeys
    }

    if (view.shownPaths) {
        return view.shownPaths!.filter(pathKey => allPathKeys.includes(pathKey))
    }

    if (view.hiddenPaths) {
        return allPathKeys.filter(pathKey => !view.hiddenPaths!.includes(pathKey) && allPathKeys.includes(pathKey))
    }

    return allPathKeys
}